/**
 * Question Type Definitions
 * Central registry mapping question types to their requirements and validation rules
 *
 * RENAMED from questionTypeRegistry.ts → questionTypeDefinitions.ts (Phase K-1)
 * to disambiguate from src/config/questionTypeRegistry.ts (runtime renderer mapping).
 *
 * This is the single source of truth for:
 * - What fields each question type requires
 * - Which renderer handles each type
 * - What visual aids are appropriate
 * - Type-specific validation rules
 */

import { QuestionType, VisualAidType } from '../types'

export interface QuestionTypeDefinition {
  type: QuestionType
  category: 'standard' | 'interactive' | 'visual-data' | 'specialized'
  renderer: string
  description: string
  requiredFields: string[]
  optionalFields: string[]
  dataStructure?: {
    required: string[]
    optional: string[]
  }
  visualRequirements?: {
    stimulus?: VisualAidType[]
    support?: VisualAidType[]
  }
  validationRules: string[]
  examples: string[]
}

/**
 * Complete Question Type Registry
 */
export const QUESTION_TYPE_REGISTRY: Record<
  QuestionType,
  QuestionTypeDefinition
> = {
  // ─── STANDARD TYPES ─────────────────────────────────────────────────────────

  multiple_choice: {
    type: 'multiple_choice',
    category: 'standard',
    renderer: 'src/components/practice/renderers/MultipleChoiceRenderer.tsx',
    description: 'Standard multiple choice with 2-5 options',
    requiredFields: ['question', 'options', 'correctAnswer'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    validationRules: [
      'options.length >= 2 && options.length <= 5',
      'correctAnswer >= 0 && correctAnswer < options.length',
      'All options must be non-empty strings',
      'No duplicate options',
    ],
    examples: ['GET-BREUK-BASIS', 'TGL-TIJD-ANALOOG', 'SWW-TT-STAM'],
  },

  // ─── INTERACTIVE TYPES ──────────────────────────────────────────────────────

  fill_in: {
    type: 'fill_in',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/FillInRenderer.tsx',
    description: 'Text input field for short answers',
    requiredFields: ['question', 'correctAnswer'],
    optionalFields: ['context', 'acceptableAnswers', 'caseSensitive'],
    dataStructure: {
      required: [],
      optional: ['acceptableAnswers', 'caseSensitive', 'placeholder'],
    },
    validationRules: [
      'correctAnswer must be a string',
      'If acceptableAnswers provided, must be array of strings',
    ],
    examples: ['SWW-TT-STAM (fill-in variant)', 'SPR-WEET-EI-IJ'],
  },

  drag_drop_fill: {
    type: 'drag_drop_fill',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/DragDropFillRenderer.tsx',
    description: 'Drag words/phrases into blanks in text',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['template', 'draggables', 'correctPlacements'],
      optional: ['distractors'],
    },
    validationRules: [
      'data.template must contain {0}, {1}, etc. placeholders',
      'data.draggables must be array of strings',
      'data.correctPlacements must map placeholder indices to draggable indices',
    ],
    examples: ['TAA-ONT-BASIS', 'SPR-KLANK-JAGER'],
  },

  matching: {
    type: 'matching',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/MatchingRenderer.tsx',
    description: 'Match items from left column to right column',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['leftItems', 'rightItems', 'correctMatches'],
      optional: ['distractors'],
    },
    validationRules: [
      'data.leftItems and data.rightItems must be arrays',
      'data.correctMatches must map left indices to right indices',
      'All indices must be valid',
    ],
    examples: ['TAA-SOORT-WW', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },

  ordering: {
    type: 'ordering',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/OrderingRenderer.tsx',
    description: 'Arrange items in correct order',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['items', 'correctOrder'],
      optional: [],
    },
    validationRules: [
      'data.items must be array of strings',
      'data.correctOrder must be array of indices',
      'correctOrder must be a permutation of [0, 1, 2, ...]',
    ],
    examples: ['STU-ALFABET', 'MET-TIJD-NAUWKEURIG'],
  },

  table_reading: {
    type: 'table_reading',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/TableReadingRenderer.tsx',
    description: 'Read and interpret data from tables',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['headers', 'rows'],
      optional: ['caption', 'highlightCell'],
    },
    validationRules: [
      'data.headers must be array of strings',
      'data.rows must be array of arrays',
      'All rows must have same length as headers',
    ],
    examples: ['STU-TABEL', 'TGL-TIJD-SCHEMA'],
  },

  number_sequence: {
    type: 'number_sequence',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/NumberSequenceRenderer.tsx',
    description: 'Complete number sequences or patterns',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['sequence', 'missingIndex', 'correctValue'],
      optional: ['rule'],
    },
    validationRules: [
      'data.sequence must be array of numbers or null',
      'data.missingIndex must be valid index',
      'data.correctValue must be a number',
    ],
    examples: ['GET-GROOT-POS', 'GET-KOMMA-GETALLENLIJN'],
  },

  ratio_table: {
    type: 'ratio_table',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/RatioTableRenderer.tsx',
    description: 'Complete ratio tables with proportional reasoning',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['headers', 'rows', 'missingCell', 'correctValue'],
      optional: [],
    },
    validationRules: [
      'data.headers must be array of 2 strings',
      'data.rows must be array of [number, number] pairs',
      'data.missingCell must be {row, col}',
      'Ratio must be consistent across rows',
    ],
    visualRequirements: {
      stimulus: ['ratio'],
      support: ['ratio'],
    },
    examples: ['GET-VERH-RECEPT', 'GET-VERH-SCHAAL'],
  },

  text_highlight: {
    type: 'text_highlight',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/TextHighlightRenderer.tsx',
    description: 'Highlight specific words or phrases in text',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['text', 'correctHighlights'],
      optional: ['maxHighlights'],
    },
    validationRules: [
      'data.text must be string',
      'data.correctHighlights must be array of {start, end} positions',
    ],
    examples: ['TAA-LEZEN-DETAIL', 'TAA-SIGNAALWOORD'],
  },

  // ─── VISUAL DATA TYPES ──────────────────────────────────────────────────────

  bar_chart: {
    type: 'bar_chart',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/BarChartRenderer.tsx',
    description: 'Read and interpret bar charts',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['data'],
      optional: [
        'title',
        'xAxisLabel',
        'yAxisLabel',
        'correctIndex',
        'hideValues',
        'barColor',
      ],
    },
    validationRules: [
      'data.data must be array of {label, value} objects',
      'Each entry must have a string label and numeric value',
      'Also accepts legacy format: data.labels + data.values arrays',
    ],
    examples: ['STU-GRAFIEK', 'STU-STAAF'],
  },

  line_chart: {
    type: 'line_chart',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/LineChartRenderer.tsx',
    description: 'Read and interpret line charts',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['data'],
      optional: [
        'title',
        'xAxisLabel',
        'yAxisLabel',
        'correctIndex',
        'hideValues',
      ],
    },
    validationRules: [
      'data.data must be array of {label, value} objects',
      'Each entry must have a string label and numeric value',
      'Also accepts legacy format: data.labels + data.values arrays',
    ],
    examples: ['STU-LIJN', 'TGL-TIJD-DIAGRAM'],
  },

  schedule_reading: {
    type: 'schedule_reading',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/ScheduleRenderer.tsx',
    description: 'Read and interpret schedules/timetables',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['headers', 'rows'],
      optional: ['title', 'highlightCell'],
    },
    validationRules: [
      'data.headers must be array of strings',
      'data.rows must be array of arrays',
    ],
    examples: ['TGL-TIJD-SCHEMA', 'TGL-TIJD-PLANNING'],
  },

  pie_chart: {
    type: 'pie_chart',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/PieChartRenderer.tsx',
    description: 'Read and interpret pie/donut charts by clicking segments',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['labels', 'values', 'correctIndex'],
      optional: ['title', 'colors', 'hideValues'],
    },
    validationRules: [
      'data.labels and data.values must have same length',
      'data.values must be array of numbers',
      'data.correctIndex must be valid index into labels/values',
      'data.labels must be array of non-empty strings',
    ],
    examples: ['STU-CIRKEL'],
  },

  // ─── SPECIALIZED TYPES ──────────────────────────────────────────────────────

  cijferend_optellen: {
    type: 'cijferend_optellen',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
    description: 'Column addition with step-by-step visualization',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['operand1', 'operand2'],
      optional: ['showSteps', 'currentStep'],
    },
    validationRules: [
      'data.operand1 and data.operand2 must be positive integers',
    ],
    visualRequirements: {
      stimulus: ['column'],
      support: ['column'],
    },
    examples: ['GET-KOMMA-PLUS', 'GET-GROOT-POS'],
  },

  cijferend_aftrekken: {
    type: 'cijferend_aftrekken',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
    description: 'Column subtraction with step-by-step visualization',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['operand1', 'operand2'],
      optional: ['showSteps', 'currentStep'],
    },
    validationRules: [
      'data.operand1 must be >= data.operand2',
      'Both operands must be positive integers',
    ],
    visualRequirements: {
      stimulus: ['column'],
      support: ['column'],
    },
    examples: ['GET-KOMMA-MIN', 'GET-GROOT-POS'],
  },

  clock: {
    type: 'clock',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ClockRenderer.tsx',
    description: 'Clock reading and time interpretation (MeesterJay 4.1)',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'options', 'correctAnswer'],
    dataStructure: {
      required: ['mode', 'target'],
      optional: ['analog', 'digital', 'options'],
    },
    validationRules: [
      'data.mode must be one of: read_analog_fill, read_digital_fill, analog_to_digital_mc, digital_to_analog_mc',
      'data.target must have hours (0-23) and minutes (0-59)',
      'If mode ends with _mc, options array is required',
    ],
    visualRequirements: {
      stimulus: ['clock'],
      support: ['clock', 'rule'],
    },
    examples: ['TGL-TIJD-ANALOOG', 'TGL-TIJD-DIGITAAL', 'TGL-TIJD-CONVERSIE'],
  },

  money: {
    type: 'money',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/MoneyAssemblerRenderer.tsx',
    description: 'Money assembly and change calculation (MeesterJay 4.1)',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'options', 'correctAnswer'],
    dataStructure: {
      required: ['mode', 'targetCents', 'allowedDenoms'],
      optional: ['priceCents', 'paidCents', 'constraints', 'options'],
    },
    validationRules: [
      'data.mode must be: make_amount or give_change',
      'data.targetCents must be positive integer',
      'data.allowedDenoms must be array of {valueCents, type, label}',
      'If mode is give_change, priceCents and paidCents are required',
    ],
    visualRequirements: {
      stimulus: ['money'],
      support: ['money', 'rule'],
    },
    examples: ['TGL-GELD-MAAK', 'TGL-GELD-TERUG', 'TGL-GELD-WISSEL'],
  },
}

/**
 * Get question type definition
 */
export function getQuestionTypeDefinition(
  type: QuestionType,
): QuestionTypeDefinition | null {
  return QUESTION_TYPE_REGISTRY[type] || null
}

/**
 * Get all question types by category
 */
export function getQuestionTypesByCategory(
  category: 'standard' | 'interactive' | 'visual-data' | 'specialized',
): QuestionTypeDefinition[] {
  return Object.values(QUESTION_TYPE_REGISTRY).filter(
    (def) => def.category === category,
  )
}

/**
 * Get question types that require specific visual aids
 */
export function getQuestionTypesRequiringVisual(
  visualType: VisualAidType,
): QuestionTypeDefinition[] {
  return Object.values(QUESTION_TYPE_REGISTRY).filter((def) => {
    const stimulus = def.visualRequirements?.stimulus ?? []
    const support = def.visualRequirements?.support ?? []
    return stimulus.includes(visualType) || support.includes(visualType)
  })
}

/**
 * Validate question type against its definition
 */
export function validateQuestionType(
  type: QuestionType,
  question: any,
): { valid: boolean; errors: string[] } {
  const def = getQuestionTypeDefinition(type)
  if (!def) {
    return { valid: false, errors: [`Unknown question type: ${type}`] }
  }

  const errors: string[] = []

  // Check required fields
  def.requiredFields.forEach((field) => {
    if (!(field in question) || question[field] === undefined) {
      errors.push(`Missing required field: ${field}`)
    }
  })

  // Check data structure if applicable
  if (def.dataStructure && question.data) {
    def.dataStructure.required.forEach((field) => {
      if (!(field in question.data) || question.data[field] === undefined) {
        errors.push(`Missing required data field: data.${field}`)
      }
    })
  }

  return { valid: errors.length === 0, errors }
}
